import { useEffect, useRef, useState } from "react";

export interface BalanceDelta {
  value: number;
  delta: number;
  direction: "up" | "down" | "none";
  isFlashing: boolean;
}

/**
 * Tracks a numeric value and computes delta/direction for animation.
 * Returns { value, delta, direction, isFlashing } where isFlashing is true
 * for a short duration after a change.
 */
export function useBalanceDelta(
  currentValue: number,
  flashDurationMs = 500,
): BalanceDelta {
  const [state, setState] = useState<BalanceDelta>({
    value: currentValue,
    delta: 0,
    direction: "none",
    isFlashing: false,
  });
  const prevValue = useRef(currentValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const prev = prevValue.current;
    if (currentValue === prev) return;

    const delta = currentValue - prev;
    const direction = delta > 0 ? "up" : delta < 0 ? "down" : "none";

    setState({
      value: currentValue,
      delta,
      direction: direction,
      isFlashing: true,
    });

    prevValue.current = currentValue;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setState((s) => ({ ...s, isFlashing: false }));
    }, flashDurationMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentValue, flashDurationMs]);

  return state;
}
